import React, { useEffect, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import {
  fetchProjectDocumentationFileBlob,
  parseDocxBlobForPreview,
} from "../../../app/utils/common/projectDocumentationFile";
import "./ProjectDocumentationDocxViewer.scss";

/**
 * Read-only Word (.docx) preview for project documentation.
 */
const ProjectDocumentationDocxViewer = ({ documentId, fileDocumentId, onLoadError, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setHtml("");
    setWarnings([]);

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(documentId, fileDocumentId);
        const { html: rendered, messages } = await parseDocxBlobForPreview(blob);
        if (!cancelled) {
          setHtml(rendered);
          setWarnings(
            (messages || [])
              .map((m) => m?.message)
              .filter(Boolean)
              .slice(0, 3)
          );
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Could not load the Word document.");
          onLoadErrorRef.current?.(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, fileDocumentId]);

  if (loading) {
    return (
      <div className={`project-doc-docx-viewer ${compact ? "project-doc-docx-viewer--compact" : ""}`}>
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`project-doc-docx-viewer ${compact ? "project-doc-docx-viewer--compact" : ""}`}>
        <Alert type="error" showIcon message={loadError} />
      </div>
    );
  }

  return (
    <div className={`project-doc-docx-viewer ${compact ? "project-doc-docx-viewer--compact" : ""}`}>
      {warnings.length > 0 ? (
        <p className="project-doc-docx-viewer__note">
          Some formatting may differ from Microsoft Word. Download the file for the original layout.
        </p>
      ) : null}
      <div
        className="project-doc-docx-viewer__content ql-editor"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default ProjectDocumentationDocxViewer;
